import { toTitleCase } from '@hozana/utils/functions/strings'

import { Locale } from 'config/types'

import type { TConnectStep } from 'modules/auth/components/ConnectForm/constants'
import type { CONNECT_METHOD } from 'modules/auth/constants'

export const GTM_DATALAYER_NAME = 'gtmDL'

/* UTMS */
/* /!\ Changes here must be changed in /src/sw/index too /!\ */
export const UTM = {
  SOURCE: {
    SHARE: 'share',
    SITE_HOZANA: 'site_Hozana',
    INTERNAL: 'interne',
  },
  MEDIUM: {
    // Share buttons
    COPYPASTE: 'copypaste',
    COPYPASTE_EMAIL: 'copypaste_email',
    FB: 'FB',
    MAILTO: 'mailto',
    MESSENGER: 'Messenger',
    PINTEREST: 'Pinterest',
    TW: 'TW',
    WHATS_APP: 'Whats_app',
    OTHER_SHARE: 'other-share',
    // Links
    APP_LANDING_PAGE: 'app_landing_page',
    APP_MENU_LINK: 'app_menu_link',
    HOMEMADE_APP_PROMPT: 'homemade app install prompt',
    HOME_PAGE: 'homepage',
    INSTAGRAM: 'Instagram',
    YOUTUBE: 'Youtube',
    TIKTOK: 'Tiktok',
    PUBLICATION_HEADER: 'publication_header',
    STANDARD_WIDGET: 'widget_standard',
  },
  CONTENT: {
    // Share buttons
    COMMUNITY_PAGE_URL_INVITE_POPIN: 'communityPageUrlInvitePopin',
    COMMUNITY_JOINED_POPIN_SHARE_BUTTON: 'communityJoinedPopinShareButton',
    HOZANA_URL_INVITE_POPIN: 'hozanaUrlInvitePopin',
    INTENTION_INVITE_BUTTON: 'intentionInviteButton',
    INTENTION_PAGE_SHARE_BUTTON: 'intentionPageShareButton',
    INVITE_TO_PRAY_INTENTIONS_MESSAGE: 'inviteToPrayIntentionsMessage',
    AFTER_PRAY_COMMUNITY_INVITE_BUTTON: 'afterPrayCommunityInviteButton',
    AFTER_PRAY_PUBLICATION_INVITE_BUTTON: 'afterPrayPublicationInviteButton',
    COMMUNITY_PRAY_SUCCESS_POPIN: 'communityPraySuccessPopin',
    PUBLICATION_BLOC_FOOTER_SHARE_BUTTON: 'publicationBlocFooterShareButton',
    PUBLICATION_PAGE_URL_INVITE_POPIN: 'publicationPageUrlInvitePopin',
    PUBLICATION_PAGE_SHARE_BUTTON: 'publicationPageShareButton',
    UNDEFINED_CONTENT_HOZANA: 'undefinedContent_hozana',
    UNDEFINED_CONTENT_PUBLICATION: 'undefinedContent_publication',
    UNDEFINED_CONTENT_COMMUNITY: 'undefinedContent_community',
    UNDEFINED_CONTENT_PRAYER_MEETING: 'undefinedContent_prayerMeeting',
    UNDEFINED_CONTENT_INTENTION: 'undefinedContent_intention',
    COMMUNITY_PAGE_HEADER_INVITE_BUTTON: 'communityPageHeaderInviteButton',
    COMMUNITY_BLOCK_INVITE_BUTTON: 'communityBlockInvitePopin',
    COMMUNITY_PAGE_INVITE_BUTTON: 'communityPageInviteButton',
    COMMUNITY_PAGE_SHARE_BUTTON: 'communityPageShareButton',
    TESTIMONY_PAGE_SHARE_BUTTON: 'testimonyPageShareButton',
    PRAYER_MEETING_SHARE_BUTTON: 'prayerMeetingShareButton',
    // Links
    NEWS: 'news',
    SHARE: 'share',
    // from mail
    PUBLICATION_MAIL_MAILTO_SHARE_BUTTON: 'publicationMailMailtoShareButton',
  },
  CAMPAIGN: {
    // Links to stores
    APP_STORE_HOZANA: 'app_store_Hozana',
    PLAY_STORE_HOZANA: 'play_store_Hozana',
    // Community or publication share buttons
    COMMUNITY: (id: number, slug: string): string => `${id}-${slug}`,
    INTENTION: (id: number): string => `Intention_${id}`,
    // Guides
    GUIDE_CONVERSION: 'guide_conversion',
  },
} as const

/* TRACKING SOURCES */

export const TRACKING_SOURCE = {
  AUTOMATIC: 'Automatic',
  CARET: 'Caret',
  COMMENT: 'Comment',
  COMMUNITIES_PAGE: 'Communities Page',
  COMMUNITY_CONFIRM_JOIN: 'Community Confirm Join',
  COMMUNITY_HEADER: 'Community Header',
  COMMUNITY_NEWSFEED: 'Community Newsfeed',
  COMMUNITY_PAGE_SEE_ALSO: 'See Also On Community Page',
  COMMUNITY_SIDEBAR: 'Community Sidebar',
  COMMUNITY_TOP_SECTION: 'Community Top Section',
  COMMUNITY_WIDGET: 'Community Widget',
  CONVERSATION: 'Conversation',
  EMAIL: 'Email',
  EMAIL_LINK: 'Email Link',
  FEED_PAGE: 'Feed Page',
  HEADER: 'Header',
  HOME_PAGE: 'Home Page',
  HP_NEWSFEED: 'Hp Newsfeed',
  HP_LOGGED: 'Hp Logged',
  HP_UNLOGGED: 'Hp Unlogged',
  INTENTION: 'Intention',
  INTENTION_PAGE: 'Intention-Page',
  INTENTION_POSTPUBCTA: 'Intention-PostPubCTA',
  INTENTIONS_PAGE_INTENTION: 'Intentions-Page-Intention',
  INTENTIONS_PAGE_BIRTHDAY: 'Intentions-Page-Birthday',
  INTENTIONS_PANEL_INTENTION: 'Intentions-Panel-Intention',
  INTENTIONS_PANEL_BIRTHDAY: 'Intentions-Panel-Birthday',
  INTENTIONS_THEMATIC_PAGE: 'Intentions-Thematic-Page',
  INTENTIONS_WIDGET: 'Intentions-Widget',
  MANAGE_COMMUNITY_LABEL: (platform: 'mobile' | 'desktop', from: 'profile' | 'feed' | 'community'): string =>
    `${toTitleCase(platform)}-${toTitleCase(from)}`,
  NAVIGATION: 'Navigation',
  POSTPRAY_PUBLICATION_BLOCK: 'Postpray Publication Block',
  POSTPRAY_SUCCESS_POPIN: 'Postpray Success Popin',
  POSTPRAY_COMMUNITY_PRAYER_BLOCK: 'Postpray Community Prayer Block',
  POSTPRAY_PUBLICATION_PAGE: 'Postpray Publication Page',
  PROFILE_PAGE: 'Profile Page',
  PROMPT_PUBLICATION_PAGE: 'Prompt On Publication Page',
  PROMPT_COMMUNITY_PAGE: 'Prompt On Community Page',
  PUBLICATION: 'Publication',
  PUBLICATION_HEADER: 'Publication Header',
  PUBLICATION_PAGE: 'Publication Page',
  PUBLICATION_SIDEBAR: 'Publication Sidebar',
  PUBLICATION_FOOTER_BANNER: 'Publication Footer Banner',
  SHARE: 'Share',
  SIDEBAR: 'Sidebar',
  SUGGESTIONS: 'Suggestions',
  SUGGESTIONS_NUM: (num: number): string => `Suggestions #${num}`,
} as const

/* AUTHENTICATION SOURCES */

/** Autentication source tracking through Analytics (Cf. general/constants - AUTH_SOURCE for authentication source tracking in database) */
export enum GTM_AUTH_SOURCE {
  HEADER_SIGNUP = 'Button Signup Header',
  HEADER_LOGIN = 'Button Login Header',
  HOME_PAGE = 'Home Page Sign Up Button',
  NAVIGATION_SIGNUP = 'Button Signup Menu',
  NAVIGATION_LOGIN = 'Button Login Menu',
  JOIN_COMMUNITY = 'Join Community',
  LEAVE_COMMUNITY = 'Leave Community',
  SHOW_COMMUNITY_NOTIFS = 'Show Community Notifications',
  ADD_FAVORITE_PUBLICATION = 'Add Favorite Publication',
  SEND_MESSAGE_BUTTON = 'Send Message Button',
  ALREADY_TRACKED = 'Already Tracked', // Must not be sent to GTM
}

/* EVENTS */

export enum EVENTS {
  PAGEVIEW = 'PAGEVIEW',

  LEAD = 'LEAD',

  CONNECT_FORM_DISPLAYED = 'CONNECT_FORM_DISPLAYED',
  CONNECT_FORM_EMAIL_SUBMITTED = 'CONNECT_FORM_EMAIL_SUBMITTED',
  SIGNUP_FORM_PASSWORD_SUBMITTED = 'SIGNUP_FORM_PASSWORD_SUBMITTED',
  SIGNUP_FORM_NAME_FILLED = 'SIGNUP_FORM_NAME_FILLED',
  SIGNUP_FORM_CAPTCHA_VALID = 'SIGNUP_FORM_CAPTCHA_VALID',
  SIGNUP_FORM_SUBMITTED = 'SIGNUP_FORM_SUBMITTED',
  SIGNUP_FORM_SUBMIT_SUCCESS = 'SIGNUP_FORM_SUBMIT_SUCCESS',
  SIGNUP_FORM_SUBMIT_FAILURE = 'SIGNUP_FORM_SUBMIT_FAILURE',
  LOGIN_METHOD_FOUND = 'LOGIN_METHOD_FOUND',
  LOGIN_FORM_SUBMITTED = 'LOGIN_FORM_SUBMITTED',
  LOGIN_FORM_SUBMIT_SUCCESS = 'LOGIN_FORM_SUBMIT_SUCCESS',
  LOGIN_FORM_SUBMIT_FAILURE = 'LOGIN_FORM_SUBMIT_FAILURE',
  LOGOUT = 'LOGOUT',

  LOST_PASSWORD_OPENED = 'LOST_PASSWORD_OPENED',
  LOST_PASSWORD_FINISHED = 'LOST_PASSWORD_FINISHED',

  RESET_PASSWORD_OPENED = 'RESET_PASSWORD_OPENED',
  RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS',

  INVITE_DISPLAYED = 'INVITE_DISPLAYED',
  INVITE_FACEBOOK = 'INVITE_FACEBOOK', // INVITE_
  INVITE_TWITTER = 'INVITE_TWITTER', // INVITE_
  INVITE_WHATSAPP = 'INVITE_WHATSAPP', // INVITE_
  INVITE_MESSENGER = 'INVITE_MESSENGER', // INVITE_
  INVITE_PINTEREST = 'INVITE_PINTEREST', // INVITE_
  INVITE_MAILTO_OPEN_POPIN = 'INVITE_MAILTO_OPEN_POPIN', // INVITE_
  INVITE_MAILTO = 'INVITE_MAILTO', // INVITE_
  INVITE_COPY = 'INVITE_COPY', // INVITE_

  PARTAGE_FACEBOOK = 'PARTAGE_FACEBOOK', // PARTAGE_
  PARTAGE_TWITTER = 'PARTAGE_TWITTER', // PARTAGE_
  PARTAGE_WHATSAPP = 'PARTAGE_WHATSAPP', // PARTAGE_
  PARTAGE_MESSENGER = 'PARTAGE_MESSENGER', // PARTAGE_
  PARTAGE_PINTEREST = 'PARTAGE_PINTEREST', // PARTAGE_
  PARTAGE_MAILTO_OPEN_POPIN = 'PARTAGE_MAILTO_OPEN_POPIN', // PARTAGE_
  PARTAGE_MAILTO = 'PARTAGE_MAILTO', // PARTAGE_
  PARTAGE_COPY = 'PARTAGE_COPY', // PARTAGE_
  PARTAGE_EMBED = 'PARTAGE_EMBED',
  PARTAGE_EMBED_COPIER = 'PARTAGE_EMBED_COPIER',

  COMMUNAUTE_CLICKED = 'COMMUNAUTE_CLICKED',
  COMMUNAUTE_CREATION = 'COMMUNAUTE_CREATION',
  COMMUNAUTE_QUITTER_CLICKED = 'COMMUNAUTE_QUITTER_CLICKED',
  COMMUNAUTE_QUITTER_DONE = 'COMMUNAUTE_QUITTER_DONE',

  PRIERE_START = 'PRIERE_START',
  PRIERE_INTERRUPTED = 'PRIERE_INTERRUPTED',
  PRIERE_FINISHED = 'PRIERE_FINISHED',

  // experimental
  MENU = 'MENU',
  CHEVRON = 'CHEVRON',

  PWA_HOMESCREEN_PROMPTED = 'PWA_HOMESCREEN_PROMPTED',
  PWA_HOMESCREEN_ADDED = 'PWA_HOMESCREEN_ADDED',
  PWA_HOMESCREEN_DISMISSED = 'PWA_HOMESCREEN_DISMISSED',

  INTENTIONS_SHUFFLE = 'INTENTIONS_SHUFFLE',
  INTENTION_ADDED = 'INTENTION_ADDED',
  INTENTION_DELETED = 'INTENTION_DELETED',

  PUSH_PERMISSION_TRIGGERED = 'PUSH_PERMISSION_TRIGGERED',
  PUSH_PERMISSION_GRANTED = 'PUSH_PERMISSION_GRANTED',
  PUSH_PERMISSION_DENIED = 'PUSH_PERMISSION_DENIED',
  PUSH_PERMISSION_IGNORED = 'PUSH_PERMISSION_IGNORED',
  ALERT_BANNER_DISPLAYED = 'ALERT_BANNER_DISPLAYED',
  ALERT_BANNER_CLOSED = 'ALERT_BANNER_CLOSED',
  ALERT_BANNER_CLICKED = 'ALERT_BANNER_CLICKED',

  COMMUNITY_PROMPT_JOIN_DISPLAYED = 'COMMUNITY_PROMPT_JOIN_DISPLAYED',
  COMMUNITY_PROMPT_JOIN_DISMISSED = 'COMMUNITY_PROMPT_JOIN_DISMISSED',
  COMMUNITY_PROMPT_JOIN_CLICK_COMMUNITY = 'COMMUNITY_PROMPT_JOIN_CLICK_COMMUNITY',
  COMMUNITY_PROMPT_JOIN_CLICK_JOIN = 'COMMUNITY_PROMPT_JOIN_CLICK_JOIN',

  MANAGE_COMMUNITY_DISPLAY_TOOLBAR = 'MANAGE_COMMUNITY_DISPLAY_TOOLBAR',
  MANAGE_COMMUNITY_SWITCH_COMMUNITY = 'MANAGE_COMMUNITY_SWITCH_COMMUNITY',
}

export enum EVENT_ORIGIN {
  AUTOMATIC = 'automatic-join', // from an automatic subscription through query parameters (ads/optimize)
  COMMUNITY_PUBLICATIONS_TAB = 'community-publications-tab', // from the publications tab of a community page
  COMMUNITY_RELATED_COMMUNITIES = 'community-related-communities', // from the related communities on community page
  COMMUNITY_POPIN_COMMUNITY = 'community-popin-community', // from the community's popin that suggests to join the community
  COMMUNITY_POPIN_RELATED_COMMUNITIES = 'community-popin-related-communities', // from the related communities on community page popin
  COMMUNITY_SHARE = 'community-share', // from a shared link to the community page
  COMMUNITY_WIDGET = 'community-widget', // from a community widget
  GUIDE_TEXT_LINK = 'guide-text-link', // from a guide card
  GUIDE_CARD = 'guide-card', // from a guide card
  GUIDE_POPIN = 'guide-popin', // from a guide popin
  GUIDE_POPIN_2 = 'guide-popin-2', // from a guide 2nd popin
  HOME_SUGGESTION = 'home-suggestion', // from the communities that are displayed on the home page
  PROFILE_ANIMATOR = 'profile-animator', // from the animator tab of a user's profile page
  PROFILE_MEMBER = 'profile-member', // from the member tab of a user's profile page
  PUBLICATION_BOTTOM_BLOCK = 'publication-bottom-block',
  PUBLICATION_OPTIONS_HEADER = 'publication-options-header',
}

export enum PUSH_EVENT_LABEL {
  CTA_ACCEPT_NOTIF = 'Cta-Accept-Notif',
  SETTINGS_PAGE = 'Settings-Page',
}

export enum PROMPT_EVENT_LABEL {
  PUBLICATION_S_MOTHER_COMMUNITY = "Publication's Mother Community",
  COMMUNITY_S_MOTHER_COMMUNITY = "Community's Mother Community",
  COMMUNITIES_LINKED_WITH_PUBLICATION = 'Communities Linked With Publication',
  COMMUNITIES_LINKED_WITH_COMMUNITY = 'Communities Linked With Community',
}

export const EVENT_LABELS = {
  PUSH: PUSH_EVENT_LABEL,
  PROMPT: PROMPT_EVENT_LABEL,
}

export enum LOGGED {
  IN = 'logged_in',
  OUT = 'logged_out',
}

enum DISPLAY_MODE {
  BROWSER = 'browser',
  PWA_ANDROID = 'pwa_android',
  PWA_IOS = 'pwa_ios',
  TWA_ANDROID = 'twa_android',
  APP_IOS = 'app_ios',
}

export enum SERVICE_WORKER {
  ACTIVE = 'sw_active',
  INACTIVE = 'sw_inactive',
}

export enum LOG_STATE {
  ONLINE = 'online',
  OFFLINE = 'offline',
}

export const DIMENSIONS = {
  LOG_STATE,
  DISPLAY_MODE,
  SERVICE_WORKER,
  LOGGED,
  LANG: Locale,
}

/**
 * These dimensions are also defined in the GTM dashboard as Variables.
 * Changes here must be mirrored there.
 */
export type TGTMDimensions = {
  isLoggedIn?: LOGGED
  displayMode?: DISPLAY_MODE
  serviceWorker?: SERVICE_WORKER
  isOnline?: LOG_STATE
  lang?: string
}

type TCommunityInfos = {
  community_id: number
  community_name: string
}

/**
 * These variables are also defined in the GTM dashboard.
 * Changes here must be mirrored there.
 */
export type TGTMVariables = {
  algo?: string
  appVisible?: boolean
  authSource?: GTM_AUTH_SOURCE
  bannerName?: string
  bannerCampaign?: string
  bannerCampaignViews?: number
  communityInfos?: TCommunityInfos
  connectMethod?: 'Loggedin' | 'Login' | 'Signup' | 'Reset-Password'
  donationSource?: 'Header'
  error?: string
  eventLabel?: PUSH_EVENT_LABEL | PROMPT_EVENT_LABEL
  fireworksClickCount?: number
  firstTime?: boolean
  firstJoin?: boolean
  from?: string
  interruptedOn?: string
  invalidStep?: TConnectStep
  method?: CONNECT_METHOD
  pageTitle?: string
  path?: string
  referrer?: string
  trackingSource?: string
  vignetPosition?: number
  wizardFirstTime?: boolean
  publicationIsAvailable?: boolean
} & TGTMDimensions
